import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { todayIn, weekday } from "../../../shared/dates";
import type { Routine, TaskRecurrence } from "../../../shared/models";
import { kidsFirst } from "../../../shared/people";
import { describeRecurrence } from "../../../shared/tasks";
import { useHousehold } from "../../app/useHousehold";
import { api } from "../../data/api";
import { useMembers, useRewards, useRoutines } from "../../data/queries";
import { Avatar } from "../../ui/Avatar";
import { Icon } from "../../ui/Icon";
import { formatReward } from "../../ui/reward";
import { Confirm, Sheet } from "../../ui/Sheet";
import { RewardStepper } from "../../widgets/tasks/TasksWidget";

type Draft = Omit<Routine, "id" | "steps" | "repeat" | "startDate"> & {
  id?: string;
  steps: { label: string; icon: string }[];
  repeat?: TaskRecurrence | null;
  startDate?: string | null;
};

type RepeatChoice = "daily" | "weekly" | "biweekly" | "monthly";
const REPEATS: { value: RepeatChoice; label: string }[] = [
  { value: "daily", label: "Every day" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Monthly" },
];
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function repeatChoice(r: TaskRecurrence | null | undefined): RepeatChoice {
  if (!r || r.kind === "daily") return "daily";
  if (r.kind === "monthly") return "monthly";
  return r.interval === 2 ? "biweekly" : "weekly";
}

/** "Evenings · every day", "Any time · Every 2 weeks · Fri" for the routine list. */
function whenText(r: Routine): string {
  const period = r.period === "morning" ? "Mornings" : r.period === "evening" ? "Evenings" : "Any time";
  return `${period} · ${describeRecurrence(r.repeat) ?? "every day"}`;
}

// One-tap starting points; parents edit from there.
const TEMPLATES: Draft[] = [
  {
    name: "Morning",
    period: "morning",
    memberIds: [],
    reward: 1,
    steps: [
      { label: "Get dressed", icon: "👕" },
      { label: "Make bed", icon: "🛏️" },
      { label: "Breakfast", icon: "🥣" },
      { label: "Brush teeth", icon: "🪥" },
      { label: "Pack backpack", icon: "🎒" },
      { label: "Shoes on", icon: "👟" },
    ],
  },
  {
    name: "Bedtime",
    period: "evening",
    memberIds: [],
    reward: 1,
    steps: [
      { label: "Tidy room", icon: "🧸" },
      { label: "Bath / shower", icon: "🛁" },
      { label: "Pajamas", icon: "🌙" },
      { label: "Brush teeth", icon: "🪥" },
      { label: "Read", icon: "📚" },
      { label: "Lights out", icon: "💡" },
    ],
  },
  {
    name: "After school",
    period: "anytime",
    memberIds: [],
    reward: 1,
    steps: [
      { label: "Unpack lunchbox", icon: "🍱" },
      { label: "Homework", icon: "✏️" },
      { label: "Practice", icon: "🎹" },
      { label: "Outside time", icon: "🌳" },
    ],
  },
];

const EMOJI = ["👕", "🛏️", "🥣", "🪥", "🎒", "👟", "🧸", "🛁", "🌙", "📚", "💡", "🍱", "✏️", "🎹", "🌳", "🐶", "🧺", "🍽️", "💊", "🧴", "💧", "🧹", "🎵", "⚽", "✓"];

export function RoutinesSection() {
  const routines = useRoutines();
  const members = useMembers().data ?? [];
  const currency = useRewards().data?.currency ?? "stars";
  const [editing, setEditing] = useState<Draft | null>(null);
  const byId = new Map(members.map((m) => [m.id, m]));
  const kids = members.filter((m) => m.isChild);

  return (
    <div className="form">
      <h3>Routines</h3>
      <p className="muted">
        Picture checklists the family taps through: every day, on certain weekdays, every other week or once a month. Finishing a whole routine can earn a reward (once a day).
      </p>
      {(routines.data ?? []).length === 0 && <p className="muted">No routines yet. Start from a template:</p>}
      <ul className="member-list">
        {(routines.data ?? []).map((r) => (
          <li key={r.id}>
            <span className="routine-admin-icons" aria-hidden="true">
              {r.steps.slice(0, 4).map((s) => s.icon).join(" ")}
            </span>
            <span>
              <strong>{r.name}</strong>
              <span className="muted small">
                {whenText(r)} · {r.steps.length} {r.steps.length === 1 ? "step" : "steps"} · {r.reward ? `${formatReward(r.reward, currency)} when done` : "no reward"} ·{" "}
                {r.memberIds.length ? r.memberIds.map((id) => byId.get(id)?.displayName).filter(Boolean).join(", ") : "all children"}
                {r.repeat?.kind === "monthly" && r.startDate ? ` · on the ${ordinal(Number(r.startDate.slice(8)))}` : ""}
              </span>
            </span>
            <button className="btn btn-sm" onClick={() => setEditing({ ...r, steps: r.steps.map((s) => ({ label: s.label, icon: s.icon })) })}>
              Edit
            </button>
          </li>
        ))}
      </ul>
      <div className="chip-row wrap">
        {TEMPLATES.map((t) => (
          <button key={t.name} className="chip" onClick={() => setEditing(structuredClone(t))}>
            <Icon name="plus" size="1rem" /> {t.name}
          </button>
        ))}
        <button className="chip" onClick={() => setEditing({ name: "", period: "anytime", memberIds: [], reward: 0, steps: [{ label: "", icon: "✓" }] })}>
          <Icon name="plus" size="1rem" /> Blank
        </button>
      </div>
      {kids.length === 0 && <p className="warn-text small">Tip: mark your kids as children in the Family section so routines know who they’re for.</p>}
      {editing && <RoutineEditor draft={editing} currency={currency} onClose={() => setEditing(null)} />}
    </div>
  );
}

function ordinal(n: number): string {
  const s = n % 100 >= 11 && n % 100 <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] ?? "th";
  return `${n}${s}`;
}

function RoutineEditor({ draft, currency, onClose }: { draft: Draft; currency: "stars" | "dollars"; onClose: () => void }) {
  const qc = useQueryClient();
  const members = useMembers().data ?? [];
  const { timeZone } = useHousehold();
  const today = todayIn(timeZone);
  const [d, setD] = useState<Draft>(draft);
  const choice = repeatChoice(d.repeat);
  const start = d.startDate ?? today;
  const weekdays = d.repeat?.kind === "weekly" ? d.repeat.weekdays : [];
  const setRepeat = (c: RepeatChoice) =>
    setD((x) => ({
      ...x,
      repeat:
        c === "daily"
          ? null
          : c === "monthly"
            ? { kind: "monthly", interval: 1 }
            : { kind: "weekly", interval: c === "biweekly" ? 2 : 1, weekdays: x.repeat?.kind === "weekly" && x.repeat.weekdays.length ? x.repeat.weekdays : [weekday(x.startDate ?? today)] },
      startDate: c === "daily" ? x.startDate : (x.startDate ?? today),
    }));
  const toggleWeekday = (i: number) =>
    setD((x) => {
      if (x.repeat?.kind !== "weekly") return x;
      const days = x.repeat.weekdays.includes(i) ? x.repeat.weekdays.filter((w) => w !== i) : [...x.repeat.weekdays, i].sort();
      return days.length ? { ...x, repeat: { ...x.repeat, weekdays: days } } : x; // at least one day
    });
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [picking, setPicking] = useState<number | null>(null);

  const setStep = (i: number, patch: Partial<Draft["steps"][number]>) => setD((x) => ({ ...x, steps: x.steps.map((s, k) => (k === i ? { ...s, ...patch } : s)) }));
  const move = (i: number, dir: -1 | 1) =>
    setD((x) => {
      const steps = [...x.steps];
      const j = i + dir;
      if (j < 0 || j >= steps.length) return x;
      [steps[i], steps[j]] = [steps[j]!, steps[i]!];
      return { ...x, steps };
    });

  const save = async () => {
    try {
      const body = {
        name: d.name,
        period: d.period,
        memberIds: d.memberIds,
        reward: d.reward,
        steps: d.steps.filter((s) => s.label.trim()),
        repeat: d.repeat ?? null,
        startDate: d.repeat ? start : (d.startDate ?? null),
      };
      await api(d.id ? `/family/routines/${d.id}` : "/family/routines", { method: d.id ? "PUT" : "POST", body });
      await Promise.all([qc.invalidateQueries({ queryKey: ["routines"] }), qc.invalidateQueries({ queryKey: ["routine-day"] })]);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const remove = async () => {
    await api(`/family/routines/${d.id}`, { method: "DELETE" });
    await Promise.all([qc.invalidateQueries({ queryKey: ["routines"] }), qc.invalidateQueries({ queryKey: ["routine-day"] })]);
    onClose();
  };

  return (
    <Sheet
      title={d.id ? `Edit ${d.name}` : "New routine"}
      onClose={onClose}
      size="lg"
      footer={
        <>
          {d.id && (
            <button className="btn btn-danger-ghost" onClick={() => setConfirm(true)}>
              Delete
            </button>
          )}
          <span className="spacer" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save}>
            Save routine
          </button>
        </>
      }
    >
      <div className="form">
        <div className="row">
          <label className="field" style={{ flex: 2 }}>
            <span>Name</span>
            <input className="input" value={d.name} maxLength={40} onChange={(e) => setD({ ...d, name: e.target.value })} placeholder="e.g. Morning" />
          </label>
          <div className="field">
            <span>When</span>
            <div className="segmented">
              {(["morning", "evening", "anytime"] as const).map((p) => (
                <button key={p} className={d.period === p ? "on" : ""} onClick={() => setD({ ...d, period: p })}>
                  {p === "morning" ? "Morning" : p === "evening" ? "Evening" : "Any time"}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="field">
          <span>Repeats</span>
          <div className="segmented">
            {REPEATS.map((r) => (
              <button key={r.value} className={choice === r.value ? "on" : ""} onClick={() => setRepeat(r.value)}>
                {r.label}
              </button>
            ))}
          </div>
          {d.repeat?.kind === "weekly" && (
            <div className="chip-row weekdays">
              {["S", "M", "T", "W", "T", "F", "S"].map((l, i) => (
                <button key={i} className={`chip round ${weekdays.includes(i) ? "on" : ""}`} onClick={() => toggleWeekday(i)} aria-label={WEEKDAYS[i]} aria-pressed={weekdays.includes(i)}>
                  {l}
                </button>
              ))}
            </div>
          )}
          {d.repeat && (
            <label className="row routine-start">
              <span className="muted small">{choice === "monthly" ? "On the day of the month of" : choice === "biweekly" ? "Starting the week of" : "Starting"}</span>
              <input className="input input-date" type="date" value={start} onChange={(e) => e.target.value && setD({ ...d, startDate: e.target.value })} />
            </label>
          )}
          {choice === "monthly" && <p className="muted small">On the {ordinal(Number(start.slice(8)))} of each month (the last day in shorter months).</p>}
        </div>
        <div className="field">
          <span>For</span>
          <div className="chip-row wrap">
            <button className={`chip ${d.memberIds.length === 0 ? "on" : ""}`} onClick={() => setD({ ...d, memberIds: [] })}>
              All children
            </button>
            {kidsFirst(members).map((m) => (
              <button
                key={m.id}
                className={`chip ${d.memberIds.includes(m.id) ? "on" : ""}`}
                onClick={() => setD({ ...d, memberIds: d.memberIds.includes(m.id) ? d.memberIds.filter((x) => x !== m.id) : [...d.memberIds, m.id] })}
              >
                <Avatar member={m} size="1.5rem" /> {m.displayName}
              </button>
            ))}
          </div>
        </div>
        <RewardStepper value={d.reward} currency={currency} onChange={(reward) => setD({ ...d, reward })} />
        <div className="field">
          <span>Steps</span>
          <ol className="step-editor">
            {d.steps.map((s, i) => (
              <li key={i}>
                <button className="emoji-btn" onClick={() => setPicking(picking === i ? null : i)} aria-label="Choose picture">
                  {s.icon}
                </button>
                <input className="input" value={s.label} maxLength={40} placeholder="Step" onChange={(e) => setStep(i, { label: e.target.value })} />
                <button className="icon-btn" onClick={() => move(i, -1)} aria-label="Move up" disabled={i === 0}>
                  <Icon name="chevronDown" className="flip" />
                </button>
                <button className="icon-btn" onClick={() => move(i, 1)} aria-label="Move down" disabled={i === d.steps.length - 1}>
                  <Icon name="chevronDown" />
                </button>
                <button className="icon-btn" onClick={() => setD({ ...d, steps: d.steps.filter((_, k) => k !== i) })} aria-label="Remove step">
                  <Icon name="trash" />
                </button>
                {picking === i && (
                  <div className="emoji-grid">
                    {EMOJI.map((e) => (
                      <button key={e} onClick={() => (setStep(i, { icon: e }), setPicking(null))}>
                        {e}
                      </button>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ol>
          {d.steps.length < 20 && (
            <button className="btn btn-sm" onClick={() => setD({ ...d, steps: [...d.steps, { label: "", icon: "✓" }] })}>
              <Icon name="plus" size="1rem" /> Add step
            </button>
          )}
        </div>
        {error && <p className="error-text">{error}</p>}
      </div>
      {confirm && <Confirm title={`Delete ${d.name}?`} message="Its history stays in the rewards log." confirmLabel="Delete" danger onCancel={() => setConfirm(false)} onConfirm={remove} />}
    </Sheet>
  );
}
